import { callable } from '../callable';
import type {
  CapNonAcademicAttestation,
  AcademicLevel,
  AlignmentMatrix,
  CapCode,
  CapComplianceReport,
  CapProposal,
  CapProposalStatus,
  CapSubmissionSummary,
  CourseCcdData,
  DeclarativeAlignmentMark,
  TimeAllocation,
} from '@dosfilos/domain';

/** Lightweight metadata for one version in a proposal's history (mirrors the
 *  application `CapProposalVersionSummary`). */
export interface CapProposalVersionSummary {
  id: string;
  version: number;
  status: CapProposalStatus;
  submittedAt?: string;
  updatedAt: string;
  active: boolean;
}

export interface ComplianceAuditRequest {
  tenantId: string;
  programId: string;
  capCode: CapCode;
  level: AcademicLevel;
  // `nonAcademic` YA NO VIAJA. Las atestaciones las LEE el servidor de donde el
  // centro las declaró; mandarlas desde acá las volvía un parámetro que el
  // cliente podía inventar, y el sello terminaba con el nombre de quien corría
  // la auditoría en vez del de quien declaró.
}

// ─── CAP (Phase E) API ──────────────────────────────────────────────────────

export interface ProgramOutcomeDto {
  id: string;
  programId: string;
  capCode: CapCode;
  sourceOutcomeId: string;
  undergraduate: { es: string; en: string };
  graduate: { es: string; en: string };
  order: number;
  updatedAt: string;
}

export interface UpsertCcdPayload {
  tenantId: string;
  /** The CCD is the card of the COURSE — one document per course, not per
   *  template (`tenants/{t}/courseCcds/{courseId}`). */
  courseId: string;
  ccd: {
    courseNumber?: string;
    capSubjectArea?: string;
    academicLevels: AcademicLevel[];
    timeAllocation: Partial<Record<AcademicLevel, TimeAllocation>>;
    tcu: Partial<Record<AcademicLevel, number>>;
    description?: string;
    deliveryFormat?: string;
    instructionalLanguage?: string;
    externalAccreditation?: string;
    clos?: { undergraduate: string[]; graduate: string[] };
  };
}

export const capApi = {
  upsertCcd: async (data: UpsertCcdPayload): Promise<{ version: number; cap: CourseCcdData }> => {
    const fn = callable<UpsertCcdPayload, { version: number; cap: CourseCcdData }>('capUpsertCcd');
    return (await fn(data)).data;
  },

  defineOutcomes: async (data: {
    tenantId: string;
    programId: string;
    capCode: CapCode;
  }): Promise<{ seeded: boolean; outcomes: ProgramOutcomeDto[] }> => {
    const fn = callable<typeof data, { seeded: boolean; outcomes: ProgramOutcomeDto[] }>(
      'capDefineOutcomes',
    );
    return (await fn(data)).data;
  },

  tagSignatureAssignment: async (data: {
    tenantId: string;
    assignmentId: string;
    isSignature: boolean;
    evaluatesOutcomeId?: string;
  }): Promise<{ isSignatureAssignment: boolean; evaluatesOutcomeId: string | null }> => {
    const fn = callable<
      typeof data,
      { isSignatureAssignment: boolean; evaluatesOutcomeId: string | null }
    >('capTagSignatureAssignment');
    return (await fn(data)).data;
  },

  assembleProposal: async (data: {
    tenantId: string;
    programId: string;
    capCode: CapCode;
    level: AcademicLevel;
  }): Promise<CapProposal> => {
    const fn = callable<typeof data, CapProposal>('capAssembleProposal');
    return (await fn(data)).data;
  },

  saveProposal: async (data: {
    tenantId: string;
    proposalId: string;
    content: Record<string, string>;
    attestations: Array<{ key: string; met: boolean }>;
  }): Promise<CapProposal> => {
    const fn = callable<typeof data, CapProposal>('capSaveProposal');
    return (await fn(data)).data;
  },

  transitionProposal: async (data: {
    tenantId: string;
    proposalId: string;
    to: 'ready' | 'submitted';
  }): Promise<CapProposal> => {
    const fn = callable<typeof data, CapProposal>('capTransitionProposal');
    return (await fn(data)).data;
  },

  getProposal: async (data: {
    tenantId: string;
    programId: string;
    capCode: CapCode;
    level: AcademicLevel;
  }): Promise<{ proposal: CapProposal | null; versions: CapProposalVersionSummary[] }> => {
    const fn = callable<
      typeof data,
      { proposal: CapProposal | null; versions: CapProposalVersionSummary[] }
    >('capGetProposal');
    return (await fn(data)).data;
  },

  reopenProposal: async (data: { tenantId: string; proposalId: string }): Promise<CapProposal> => {
    const fn = callable<typeof data, CapProposal>('capReopenProposal');
    return (await fn(data)).data;
  },

  /** Presenta la propuesta a la red TMAI (Fase 2). Lo que se publica lo arma el
   *  servidor: acá solo viaja QUÉ propuesta y si es consulta o solicitud. */
  /** Presentar en un acto: el servidor ensambla contra la última auditoría,
   *  valida, congela y crea la ronda. Reemplaza la secuencia que el centro
   *  ejecutaba a mano (re-ensamblar → marcar lista → someter → presentar). */
  presentProposal: async (data: {
    tenantId: string;
    proposalId: string;
    submissionKind: 'advisory' | 'formal';
    attestations?: { key: 'faculty_cvs_attached' | 'submitting_with_known_gaps'; met: boolean }[];
  }): Promise<CapSubmissionSummary> => {
    const fn = callable<typeof data, CapSubmissionSummary>('capPresentProposal');
    return (await fn(data)).data;
  },

  submitProposalToNetwork: async (data: {
    tenantId: string;
    proposalId: string;
    submissionKind: 'advisory' | 'formal';
  }): Promise<CapSubmissionSummary> => {
    const fn = callable<typeof data, CapSubmissionSummary>('capSubmitProposalToNetwork');
    return (await fn(data)).data;
  },

  runComplianceAudit: async (
    data: ComplianceAuditRequest,
  ): Promise<Omit<CapComplianceReport, 'id'>> => {
    const fn = callable<ComplianceAuditRequest, Omit<CapComplianceReport, 'id'>>(
      'capRunComplianceAudit',
    );
    return (await fn(data)).data;
  },

  saveComplianceSnapshot: async (
    data: ComplianceAuditRequest,
  ): Promise<{ id: string; report: CapComplianceReport }> => {
    const fn = callable<ComplianceAuditRequest, { id: string; report: CapComplianceReport }>(
      'capSaveComplianceSnapshot',
    );
    return (await fn(data)).data;
  },

  /** Las cuatro declaraciones no académicas, siempre las cuatro. */
  getNonAcademicAttestations: async (data: {
    tenantId: string;
    programId: string;
    capCode: CapCode;
    level: AcademicLevel;
  }): Promise<{ attestations: CapNonAcademicAttestation[] }> => {
    const fn = callable<typeof data, { attestations: CapNonAcademicAttestation[] }>(
      'capGetNonAcademicAttestations',
    );
    return (await fn(data)).data;
  },

  /** Declara UNA. Queda guardada con quién la declaró y cuándo. */
  setNonAcademicAttestation: async (data: {
    tenantId: string;
    programId: string;
    capCode: CapCode;
    level: AcademicLevel;
    key: CapNonAcademicAttestation['key'];
    met: boolean;
  }): Promise<CapNonAcademicAttestation> => {
    const fn = callable<typeof data, CapNonAcademicAttestation>('capSetNonAcademicAttestation');
    return (await fn(data)).data;
  },

  listComplianceSnapshots: async (data: {
    tenantId: string;
    programId: string;
    capCode: CapCode;
  }): Promise<{ snapshots: CapComplianceReport[] }> => {
    const fn = callable<typeof data, { snapshots: CapComplianceReport[] }>(
      'capListComplianceSnapshots',
    );
    return (await fn(data)).data;
  },

  buildAlignmentMatrix: async (data: {
    tenantId: string;
    programId: string;
    capCode: CapCode;
  }): Promise<AlignmentMatrix> => {
    const fn = callable<typeof data, AlignmentMatrix>('capBuildAlignmentMatrix');
    return (await fn(data)).data;
  },

  setAlignmentMarks: async (data: {
    tenantId: string;
    courseId: string;
    /** The program whose PLOs the marks belong to — the matrix screen is per
     *  program, so it already knows. */
    programId: string;
    alignment: Record<string, DeclarativeAlignmentMark[]>;
  }): Promise<{ version: number; alignment: Record<string, DeclarativeAlignmentMark[]> }> => {
    const fn = callable<
      typeof data,
      { version: number; alignment: Record<string, DeclarativeAlignmentMark[]> }
    >('capSetAlignmentMarks');
    return (await fn(data)).data;
  },

  updateOutcome: async (data: {
    tenantId: string;
    outcomeId: string;
    undergraduate: { es: string; en: string };
    graduate: { es: string; en: string };
    order?: number;
  }): Promise<{ outcome: ProgramOutcomeDto }> => {
    const fn = callable<typeof data, { outcome: ProgramOutcomeDto }>('capUpdateOutcome');
    return (await fn(data)).data;
  },
};
